import { useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  useCreateFreshStrawEmbryoPartnershipMutation,
  useDeleteFreshStrawEmbryoPartnershipMutation,
  useEndAllFreshStrawEmbryoPartnershipsMutation,
  useGetFreshStrawEmbryoPartnershipsQuery,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FreshStrawPartnershipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  embryoId: string | null;
  embryoLabel?: string;
}

export function FreshStrawPartnershipDialog({
  open,
  onOpenChange,
  embryoId,
  embryoLabel,
}: FreshStrawPartnershipDialogProps) {
  const dispatch = useDispatch();
  const [accountId, setAccountId] = useState('');
  const [percent, setPercent] = useState('');

  const partnershipsQuery = useGetFreshStrawEmbryoPartnershipsQuery(
    { embryoId: embryoId ?? '' },
    { skip: !open || !embryoId },
  );

  const [createPartnership, createState] =
    useCreateFreshStrawEmbryoPartnershipMutation();
  const [deletePartnership] = useDeleteFreshStrawEmbryoPartnershipMutation();
  const [endAllPartnerships, endAllState] =
    useEndAllFreshStrawEmbryoPartnershipsMutation();

  const rows = partnershipsQuery.data ?? [];

  const reset = () => {
    setAccountId('');
    setPercent('');
  };

  const handleCreate = async () => {
    if (!embryoId || !accountId || !percent) return;
    const pct = Number(percent);
    if (!Number.isFinite(pct)) return;
    try {
      await createPartnership({ embryoId, accountId, percent: pct }).unwrap();
      reset();
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not add partnership',
          detail: extractMessage(err),
          life: ToastLife.long,
        }),
      );
    }
  };

  const handleDelete = async (partnershipId: string) => {
    if (!embryoId) return;
    try {
      await deletePartnership({ embryoId, partnershipId }).unwrap();
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not remove partnership',
          detail: extractMessage(err),
          life: ToastLife.long,
        }),
      );
    }
  };

  const handleEndAll = async () => {
    if (!embryoId) return;
    try {
      await endAllPartnerships({ embryoId }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Partnerships ended',
          detail: 'All active partnerships on this embryo are now closed.',
          life: ToastLife.short,
        }),
      );
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not end partnerships',
          detail: extractMessage(err),
          life: ToastLife.long,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Partnerships{embryoLabel ? ` · ${embryoLabel}` : ''}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <div className="rounded border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
              Current partnerships
            </div>
            {rows.length === 0 ? (
              <div className="p-3 text-xs text-gray-500">
                No active partnerships.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600">
                  <tr>
                    <th className="px-2 py-1">Account</th>
                    <th className="px-2 py-1">Percent</th>
                    <th className="px-2 py-1">Begin</th>
                    <th className="px-2 py-1">End</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.partnershipId}
                      className="border-t border-gray-100"
                    >
                      <td className="px-2 py-1">
                        {r.accountCode ?? ''} {r.accountName ?? ''}
                      </td>
                      <td className="px-2 py-1">{r.percent ?? ''}%</td>
                      <td className="px-2 py-1 text-xs text-gray-600">
                        {r.beginDate?.slice(0, 10) ?? ''}
                      </td>
                      <td className="px-2 py-1 text-xs text-gray-600">
                        {r.endDate?.slice(0, 10) ?? ''}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(r.partnershipId)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 p-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Account GUID
              </label>
              <input
                type="text"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="min-w-[320px] rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">
                Percent
              </label>
              <input
                type="number"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
            <Button
              onClick={handleCreate}
              disabled={!accountId || !percent || createState.isLoading}
            >
              Add partnership
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleEndAll}
            disabled={rows.length === 0 || endAllState.isLoading}
          >
            End all partnerships
          </Button>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function extractMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  return 'Unexpected error.';
}
